import { useTranslation } from "@sagot/i18n";
import { Button, Dialog, DialogContent, Icon, toast } from "@sagot/ui";
import { useState } from "react";
import { useErrorMessage } from "../../../lib/api-errors";
import type { SourceView } from "../shared/types";
import { AudiencePicker } from "./audience-picker";
import { useDeleteSource, useUpdateAudiences } from "./use-knowledge";

const K = "deployKnowledge.knowledge";
const PUBLIC_AUDIENCE = 1;

interface SourceDialogProps {
  source: SourceView;
  onClose: () => void;
}

/** "Change access level": audience chips → `PATCH /v1/sources/{id} {audiences}` (≥ 1 audience, DEC-024). */
export function ChangeAccessDialog({ source, onClose }: SourceDialogProps) {
  const { t } = useTranslation();
  const message = useErrorMessage();
  const update = useUpdateAudiences();
  const [audiences, setAudiences] = useState<number[]>(source.audiences);
  const [error, setError] = useState<string | undefined>();
  const save = () => {
    if (audiences.length === 0) {
      setError(t(`${K}.add.audiencesRequired`));
      return;
    }
    update.mutate(
      { sourceId: source.id, audiences },
      {
        onSuccess: () => {
          toast.success(t(`${K}.access.saved`, { count: source.chunk_count }));
          onClose();
        },
        onError: (caught) => toast.error(message(caught)),
      },
    );
  };
  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent
        className="max-w-md"
        title={t(`${K}.access.title`)}
        description={t(`${K}.access.description`, { name: source.title })}
        closeLabel={t("common.close")}
        footer={
          <>
            <Button variant="ghost" onClick={onClose}>
              {t("common.cancel")}
            </Button>
            <Button loading={update.isPending} onClick={save}>
              {t(`${K}.access.save`)}
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <AudiencePicker value={audiences} onChange={(next) => { setAudiences(next); setError(undefined); }} error={error} />
          <p className="text-xs text-fg-subtle">{source.agent_count > 0 ? t("build.agentKnowledge.appliesAll", { count: source.agent_count }) : t("build.agentKnowledge.appliesNone")}</p>
          {audiences.includes(PUBLIC_AUDIENCE) && source.pii_found.length > 0 ? (
            <p role="status" className="flex items-start gap-2 rounded-md bg-accent-soft p-3 text-xs text-accent-ink">
              <Icon name="alert-triangle" className="mt-0.5 size-4 shrink-0" />
              {t(`${K}.access.piiWarning`, { name: source.title, kinds: source.pii_found.join(", ") })}
            </p>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** "Delete this source?" → `DELETE /v1/sources/{id}`. */
export function DeleteSourceDialog({ source, onClose }: SourceDialogProps) {
  const { t } = useTranslation();
  const message = useErrorMessage();
  const remove = useDeleteSource();
  const confirm = () => {
    remove.mutate(source.id, {
      onSuccess: () => {
        toast.success(t(`${K}.delete.deleted`));
        onClose();
      },
      onError: (caught) => toast.error(message(caught)),
    });
  };
  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent
        className="max-w-md"
        title={t(`${K}.delete.title`)}
        closeLabel={t("common.close")}
        footer={
          <>
            <Button variant="ghost" onClick={onClose}>
              {t("common.cancel")}
            </Button>
            <Button variant="danger" loading={remove.isPending} onClick={confirm}>
              <Icon name="trash" />
              {t(`${K}.delete.confirm`)}
            </Button>
          </>
        }
      >
        <div className="flex items-start gap-3">
          <span className="inline-flex size-10 shrink-0 items-center justify-center rounded-pill bg-danger-soft text-danger">
            <Icon name="trash" className="size-5" />
          </span>
          <div>
            <p className="font-semibold text-fg">{source.title}</p>
            <p className="mt-1 text-sm text-fg-muted">{t(`${K}.delete.body`, { count: source.chunk_count })}</p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
